import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useGetBranding } from "@workspace/api-client-react";
import { METALUI_BRAND } from "@workspace/brand";
import { useMe } from "@/lib/auth";
import { getSelectedTenantId } from "@/lib/tenant-selection";
import { isLegacyPortalPortfolioPath, isPortfolioPath } from "@/lib/app-surface";

export type Branding = {
  appName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  // Colour for the app-name wordmark in the sidebar header. Null = inherit
  // `text-sidebar-primary` (the previous behaviour). Consumed as a raw
  // hex inline `color:` in `main-layout.tsx`, NOT through a CSS var —
  // nothing else needs it and that keeps the platform default branch
  // (no inline override) one less special case.
  appNameColor: string | null;
  // Extended palette (May 2026 — Brand Options page). Each null = "use the
  // active theme default" (the Tailwind/shadcn variables in index.css).
  accentColor: string | null;
  destructiveColor: string | null;
  successColor: string | null;
  warningColor: string | null;
  backgroundColor: string | null;
  foregroundColor: string | null;
  mutedColor: string | null;
  borderColor: string | null;
  sidebarColor: string | null;
  sidebarForegroundColor: string | null;
  sidebarAccentColor: string | null;
  // Chart palette + visited links (May 2026 — operator-requested chart
  // customisation; covers Recharts colours on /reports/profit-loss etc.).
  chartColor1: string | null;
  chartColor2: string | null;
  chartColor3: string | null;
  chartColor4: string | null;
  chartColor5: string | null;
  linkVisitedColor: string | null;
};

// Order matters here only for cache hydration (extended fields tolerate
// undefined / null). Used by readCachedBranding to materialise prior values.
const EXTENDED_COLOR_KEYS = [
  "accentColor",
  "destructiveColor",
  "successColor",
  "warningColor",
  "backgroundColor",
  "foregroundColor",
  "mutedColor",
  "borderColor",
  "sidebarColor",
  "sidebarForegroundColor",
  "sidebarAccentColor",
  "chartColor1",
  "chartColor2",
  "chartColor3",
  "chartColor4",
  "chartColor5",
  "linkVisitedColor",
] as const;

// CSS variable mapping for each extended palette field. Keeping it in one
// table means adding a new field is a one-line change in three places
// (schema, this map, the page form).
//
// Each value is the list of `--*` variables to set on document.documentElement.
// Some brand fields fan out to multiple vars (e.g. `primaryColor` also drives
// `--ring` so focus rings re-tint).
//
// IMPORTANT — theme-agnostic only (May 2026 light-mode-readability fix):
//   We deliberately do NOT include `backgroundColor` / `foregroundColor` /
//   `mutedColor` / `borderColor` / `sidebarColor` / `sidebarForegroundColor`
//   / `sidebarAccentColor` here, even though those columns still exist in
//   `app_branding` and on the `Branding` type for back-compat.
//
//   Reason: those vars are defined twice in `index.css` — once under `:root`
//   (light) and once under `.dark` (dark). An inline `:root` override wins
//   over BOTH, so a single hex value cannot possibly be correct for both
//   themes. Operators historically picked a colour that looked good in
//   their dominant theme (e.g. gold `#f5bd05` for foreground in dark mode)
//   and the other theme became unreadable (gold-on-ivory in light mode =
//   "I can't read anything"). The Brand Options page no longer offers
//   these fields; saved values for them are kept in the DB but ignored
//   here. If we need per-tenant theme tweaks later we'll add separate
//   light + dark variants in the schema.
const COLOR_VAR_MAP: Record<string, string[]> = {
  accentColor: ["--accent"],
  destructiveColor: ["--destructive"],
  successColor: ["--success"],
  warningColor: ["--warning"],
  // Chart palette → Recharts via `hsl(var(--chart-N))` in profit-loss.tsx
  // and elsewhere. Setting an inline override on :root wins over the
  // stylesheet :root rule; clearing removes the override and falls back.
  chartColor1: ["--chart-1"],
  chartColor2: ["--chart-2"],
  chartColor3: ["--chart-3"],
  chartColor4: ["--chart-4"],
  chartColor5: ["--chart-5"],
  // Visited links — index.css has `a:visited { color: hsl(var(--link-visited, var(--primary))); }`
  // so clearing this falls back to the primary brand colour automatically.
  linkVisitedColor: ["--link-visited"],
};

// Platform-default branding (MetalUI). Used on auth screens /
// pending-approval where no tenant is bound yet, and as the very first
// paint for users who have never loaded the app on this device. Returning
// users hydrate from the localStorage cache below — see CACHE_KEY — so
// they see THEIR tenant's brand on the first frame instead of a flash of
// the platform default.
//
// Platform identity is MetalUI — Metal Blue primary (#1E3A8A) + Steel Gray
// secondary (#4B5563) with Metal Gold (#D4AF37) as the accent/ring colour
// defined via CSS variables in `index.css`. The CSS handles light/dark
// variation independently:
//   - Light: `--primary` = Metal Blue, `--ring` = Metal Gold
//   - Dark:  `--primary` = Metal Gold (CTA), `--ring` = Metal Blue
// `applyBranding` below sets `--primary` + `--ring` to `primaryColor`
// (Metal Blue) via inline style on `:root`, so both modes use Metal Blue as
// the interactive primary when no tenant brand is active. Each tenant
// overrides via the `app_branding` row that `/api/branding` returns.
const PLATFORM_DEFAULT_BRANDING: Branding = {
  appName: METALUI_BRAND.appName,
  logoUrl: null,
  // These two fields come from the single source of truth (`@workspace/brand`),
  // shared with METALUI_DEFAULTS in `artifacts/api-server/src/routes/branding.ts`
  // and the column-level defaults in `lib/db/src/schema/branding.ts`.
  // Diverging here causes a visible first-paint flash for unconfigured tenants.
  //   primaryColor  = Metal Blue  #1E3A8A  → `--primary` / `--ring` via applyBranding
  //   secondaryColor = Steel Gray #4B5563  → `--secondary` / `--sidebar-accent`
  primaryColor: METALUI_BRAND.primaryColor,
  secondaryColor: METALUI_BRAND.secondaryColor,
  appNameColor: null,
  accentColor: null,
  destructiveColor: null,
  successColor: null,
  warningColor: null,
  backgroundColor: null,
  foregroundColor: null,
  mutedColor: null,
  borderColor: null,
  sidebarColor: null,
  sidebarForegroundColor: null,
  sidebarAccentColor: null,
  chartColor1: null,
  chartColor2: null,
  chartColor3: null,
  chartColor4: null,
  chartColor5: null,
  linkVisitedColor: null,
};

// Last-known tenant branding cache. Eliminates the platform-default flash
// for returning users: on cold load we read the cached values synchronously
// before first paint, applyBranding() runs immediately, and by the time
// /api/branding responds the values usually match (no second repaint).
// Pre-auth users have no cache → see MetalUI default, which is correct.
// Cache is keyed PER TENANT (`<prefix>:<tenantId>`) so one tenant's brand can
// never bleed into another's on the same browser — the root cause of the
// cross-tenant "wrong app name/logo" bug. There is deliberately NO global
// "last active tenant" pointer: at module-eval / first-paint time we do not yet
// know which tenant this session belongs to (`useMe` hasn't resolved), and
// guessing from a global pointer is exactly how a prior tenant's brand leaks.
// So first paint always starts from the platform default and the provider
// hydrates from THIS tenant's cache only once `scopeTenantId` is known. The
// legacy global key `vc-branding-cache-v1` is abandoned (never read).
const CACHE_PREFIX = "vc-branding-cache-v2";

function cacheKeyFor(tenantId: string | null | undefined): string | null {
  return tenantId ? `${CACHE_PREFIX}:${tenantId}` : null;
}

function readCachedBranding(tenantId: string | null | undefined): Branding | null {
  if (typeof window === "undefined") return null;
  const key = cacheKeyFor(tenantId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Branding>;
    if (!parsed.appName || !parsed.primaryColor || !parsed.secondaryColor) return null;
    const out: Branding = {
      appName: parsed.appName,
      logoUrl: parsed.logoUrl ?? null,
      primaryColor: parsed.primaryColor,
      secondaryColor: parsed.secondaryColor,
      appNameColor: typeof parsed.appNameColor === "string" ? parsed.appNameColor : null,
      accentColor: null,
      destructiveColor: null,
      successColor: null,
      warningColor: null,
      backgroundColor: null,
      foregroundColor: null,
      mutedColor: null,
      borderColor: null,
      sidebarColor: null,
      sidebarForegroundColor: null,
      sidebarAccentColor: null,
      chartColor1: null,
      chartColor2: null,
      chartColor3: null,
      chartColor4: null,
      chartColor5: null,
      linkVisitedColor: null,
    };
    for (const k of EXTENDED_COLOR_KEYS) {
      const v = parsed[k];
      if (typeof v === "string") out[k] = v;
    }
    return out;
  } catch {
    return null;
  }
}

function writeCachedBranding(b: Branding, tenantId: string | null | undefined) {
  if (typeof window === "undefined") return;
  const key = cacheKeyFor(tenantId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(b));
  } catch {
    // Quota errors etc. are non-fatal — flicker is recoverable.
  }
}

// ---------------------------------------------------------------------------
// Subdomain pre-auth branding
// ---------------------------------------------------------------------------
// On `<slug>.metalui.co.uk` the user may land on the sign-in page BEFORE
// Clerk establishes a session and before `useMe` resolves. We detect the
// subdomain at module-eval time (synchronous, no async needed), then fetch
// the public `/api/public/branding?slug=` endpoint from a useEffect so the
// sign-in screen already shows the tenant's colours, logo, and app name on
// first paint rather than the generic MetalUI defaults.
//
// A separate localStorage cache (`vc-subdomain-branding-v1:<slug>`) backs
// the synchronous initial state — returning users get the right brand on
// the very first frame with zero round-trips. The per-tenant cache
// (`vc-branding-cache-v2:<tenantId>`) is left untouched; it takes over
// once the user is logged in and `scopeTenantId` is known.

const SUBDOMAIN_CACHE_PREFIX = "vc-subdomain-branding-v1";

/**
 * Extract the MetalUI tenant slug from the current hostname, or return null
 * if this is not a recognised MetalUI subdomain. Mirrors the server-side
 * `resolveSlugFromHost` in `middleware/subdomainTenant.ts`.
 *
 * Called once at module-eval time (synchronous) so the result is available
 * immediately for cache hydration. Safe to call in SSR / non-browser
 * environments — returns null when `window` is not defined.
 */
function resolveSubdomainSlug(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  const suffix = `.${METALUI_BRAND.platformDomain}`;
  if (!hostname.endsWith(suffix)) return null;
  const slug = hostname.slice(0, hostname.length - suffix.length);
  return slug && /^[a-z0-9-]+$/.test(slug) ? slug : null;
}

function subdomainCacheKeyFor(slug: string): string {
  return `${SUBDOMAIN_CACHE_PREFIX}:${slug}`;
}

function readCachedSubdomainBranding(slug: string): Branding | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(subdomainCacheKeyFor(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Branding>;
    if (!parsed.appName || !parsed.primaryColor || !parsed.secondaryColor) return null;
    const out: Branding = {
      appName: parsed.appName,
      logoUrl: parsed.logoUrl ?? null,
      primaryColor: parsed.primaryColor,
      secondaryColor: parsed.secondaryColor,
      appNameColor: typeof parsed.appNameColor === "string" ? parsed.appNameColor : null,
      accentColor: null,
      destructiveColor: null,
      successColor: null,
      warningColor: null,
      backgroundColor: null,
      foregroundColor: null,
      mutedColor: null,
      borderColor: null,
      sidebarColor: null,
      sidebarForegroundColor: null,
      sidebarAccentColor: null,
      chartColor1: null,
      chartColor2: null,
      chartColor3: null,
      chartColor4: null,
      chartColor5: null,
      linkVisitedColor: null,
    };
    for (const k of EXTENDED_COLOR_KEYS) {
      const v = parsed[k];
      if (typeof v === "string") out[k] = v;
    }
    return out;
  } catch {
    return null;
  }
}

function writeCachedSubdomainBranding(b: Branding, slug: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(subdomainCacheKeyFor(slug), JSON.stringify(b));
  } catch {
    // Quota errors etc. are non-fatal.
  }
}

/**
 * Coerce a raw `/api/public/branding` response body into a `Branding`
 * object, falling back to the platform default for any missing/invalid field.
 */
function shapeBrandingFromPublic(d: Partial<Branding>): Branding {
  const out: Branding = {
    appName: d.appName || PLATFORM_DEFAULT_BRANDING.appName,
    logoUrl: d.logoUrl ?? null,
    primaryColor: d.primaryColor || PLATFORM_DEFAULT_BRANDING.primaryColor,
    secondaryColor: d.secondaryColor || PLATFORM_DEFAULT_BRANDING.secondaryColor,
    appNameColor: typeof d.appNameColor === "string" && d.appNameColor ? d.appNameColor : null,
    accentColor: null,
    destructiveColor: null,
    successColor: null,
    warningColor: null,
    backgroundColor: null,
    foregroundColor: null,
    mutedColor: null,
    borderColor: null,
    sidebarColor: null,
    sidebarForegroundColor: null,
    sidebarAccentColor: null,
    chartColor1: null,
    chartColor2: null,
    chartColor3: null,
    chartColor4: null,
    chartColor5: null,
    linkVisitedColor: null,
  };
  for (const k of EXTENDED_COLOR_KEYS) {
    const v = d[k];
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

// Module-level constants — computed once at import time (synchronous).
// `SUBDOMAIN_SLUG` is null on the main *.replit.app / custom domains where
// the full auth flow runs normally; non-null only on `<slug>.metalui.co.uk`.
const SUBDOMAIN_SLUG: string | null = resolveSubdomainSlug();
const INITIAL_SUBDOMAIN_BRANDING: Branding | null = SUBDOMAIN_SLUG
  ? readCachedSubdomainBranding(SUBDOMAIN_SLUG)
  : null;

// First paint ALWAYS starts from the platform default — we cannot know which
// tenant this session belongs to before `useMe` resolves, so we never guess
// from any cross-tenant pointer. Once the provider learns `scopeTenantId` it
// hydrates from that tenant's own cache (instant, no /api/branding round-trip),
// which means a returning same-tenant user only briefly shows the default
// before snapping to their brand — and a different tenant NEVER sees a prior
// tenant's brand, not even for one frame.
//
// Exception: on a `<slug>.metalui.co.uk` subdomain we apply the
// per-slug subdomain cache synchronously so returning visitors see the right
// brand before Clerk even starts.
const INITIAL_BRANDING: Branding = INITIAL_SUBDOMAIN_BRANDING ?? PLATFORM_DEFAULT_BRANDING;

const BrandingContext = createContext<Branding>(INITIAL_BRANDING);

/**
 * Convert a stored object-storage path (e.g. "/objects/<uuid>") into a
 * URL the browser can actually fetch. The api-server mounts the object
 * route at `${BASE_URL}api/storage/objects/<uuid>`, so we prefix it.
 * Pass-through for absolute URLs (http(s)) and null/empty.
 */
export function resolveLogoSrc(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  // Tenant-public canonical path: `/public-objects/<tenantId>/<filename>` →
  // `/api/storage/public/<tenantId>/<filename>` (auth-free serve route added
  // in Task #6 tenant-public bucket). Branding logo uploads moved to
  // `useUpload({mode:"public"})` and now return this shape; without this
  // branch the browser tries to fetch the path relative to the current page
  // and 404s — see May 2026 premium-numismatics broken-logo incident.
  if (logoUrl.startsWith("/public-objects/")) {
    return `${import.meta.env.BASE_URL}api/storage/public${logoUrl.slice("/public-objects".length)}`;
  }
  if (logoUrl.startsWith("/objects/")) {
    return `${import.meta.env.BASE_URL}api/storage${logoUrl}`;
  }
  return logoUrl;
}

// Convert "#RRGGBB" → "H S% L%" string for use as a CSS HSL channel value
// (the design system uses `hsl(var(--primary))` etc).
function hexToHsl(hex: string): string | null {
  const m = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.exec(hex);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let H = 0;
  let S = 0;
  const L = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: H = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: H = ((b - r) / d + 2); break;
      case b: H = ((r - g) / d + 4); break;
    }
    H *= 60;
  }
  return `${H.toFixed(0)} ${(S * 100).toFixed(0)}% ${(L * 100).toFixed(0)}%`;
}

function applyBranding(b: Branding, updateDocumentTitle = true) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const primaryHsl = hexToHsl(b.primaryColor);
  const secondaryHsl = hexToHsl(b.secondaryColor);
  if (primaryHsl) {
    root.style.setProperty("--primary", primaryHsl);
    root.style.setProperty("--ring", primaryHsl);
    root.style.setProperty("--sidebar-primary", primaryHsl);
    root.style.setProperty("--sidebar-ring", primaryHsl);
  }
  if (secondaryHsl) {
    root.style.setProperty("--secondary", secondaryHsl);
    // --sidebar-accent always falls back to secondary now. The retired
    // `sidebarAccentColor` field used to gate this branch (operators
    // could override it independently), but it was dropped in May 2026
    // along with the rest of the structural Sidebar/Surface palette —
    // see COLOR_VAR_MAP comment above. Reading `b.sidebarAccentColor`
    // here would mean a legacy non-null value still changes runtime
    // styling, defeating the "ignored at render time" guarantee.
    root.style.setProperty("--sidebar-accent", secondaryHsl);
  }

  // Extended palette. For each configured field, set every CSS var it
  // maps to. When the field is null/empty we REMOVE the inline override
  // so the rule falls back to the stylesheet :root default — this makes
  // "Reset to default" take effect immediately without a page reload.
  for (const key of EXTENDED_COLOR_KEYS) {
    const hex = b[key];
    const vars = COLOR_VAR_MAP[key] ?? [];
    if (!hex) {
      for (const cssVar of vars) root.style.removeProperty(cssVar);
      continue;
    }
    const hsl = hexToHsl(hex);
    if (!hsl) continue;
    for (const cssVar of vars) root.style.setProperty(cssVar, hsl);
  }

  const pathname = window.location.pathname;
  if (
    updateDocumentTitle &&
    b.appName &&
    !isPortfolioPath(pathname) &&
    !isLegacyPortalPortfolioPath(pathname)
  ) {
    document.title = b.appName;
  }
}

// Point the installable-PWA surfaces at the CURRENT tenant's brand so the
// Home Screen app shows their name + logo + theme colour rather than the
// static MetalUI defaults baked into index.html / the static manifest.
//
//   - `<link rel="manifest">` is swapped to the dynamic, per-tenant endpoint
//     (`/api/manifest.webmanifest?tenant=<id>`) which serves the tenant's
//     name/colour/icons (Android/Chrome install). With no tenant yet we point
//     at the same endpoint without a param, which returns the MetalUI
//     platform default — identical to the static file.
//   - `<meta name="theme-color">` + `apple-mobile-web-app-title` and the
//     `apple-touch-icon` drive the iOS "Add to Home Screen" name + icon, which
//     Safari reads from the live DOM at install time.
//
// The apple-touch-icon is only repointed at a tenant logo that can be fetched
// WITHOUT a session (absolute URL or tenant-public object) and is a raster
// image — a private `/objects/` logo or an SVG would render a broken/blank
// icon, so we keep the default in those cases.
//
// The `<link rel="icon">` (browser tab favicon) is updated via the same
// public-logo guard so subdomain visitors see the tenant logo in their tab.
function applyPwaHead(b: Branding, tenantId: string | null | undefined) {
  if (typeof document === "undefined") return;
  const base = import.meta.env.BASE_URL; // always ends with "/"

  // The manifest endpoint needs the app base so its start_url/scope + default
  // icon URLs anchor correctly under a non-root BASE_PATH.
  const baseParam = `base=${encodeURIComponent(base)}`;

  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifestLink) {
    const portfolioSurface =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith(`${base}portfolio`);
    const surfaceParam = portfolioSurface ? "&surface=portfolio" : "";
    const href = tenantId
      ? `${base}api/manifest.webmanifest?tenant=${encodeURIComponent(tenantId)}&${baseParam}`
      : `${base}api/manifest.webmanifest?${baseParam}`;
    const surfacedHref = `${href}${surfaceParam}`;
    if (manifestLink.getAttribute("href") !== surfacedHref) manifestLink.setAttribute("href", surfacedHref);
  }

  const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeMeta && b.primaryColor) themeMeta.setAttribute("content", b.primaryColor);

  const titleMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]',
  );
  if (titleMeta && b.appName) titleMeta.setAttribute("content", b.appName);

  // Determine if the logo URL is publicly fetchable (absolute URL or
  // tenant-public object path) and is a raster image. Used for both the
  // apple-touch-icon AND the browser-tab favicon (<link rel="icon">).
  const logo = b.logoUrl;
  const publicFetchable =
    !!logo && (/^https?:\/\//i.test(logo) || logo.startsWith("/public-objects/"));
  const src = publicFetchable ? resolveLogoSrc(logo) : null;
  const isRaster = !!src && !/\.svg(\?|$)/i.test(src);

  const appleIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleIcon) {
    // Use a tenant logo only when it's a publicly-fetchable raster image
    // (Safari fetches the icon without a session at install time, and iOS
    // ignores SVG here). Otherwise restore the bundled default so switching
    // tenants — or clearing a previously-set logo — never leaves a stale icon.
    const next = isRaster ? src! : `${base}icons/apple-touch-icon.png`;
    if (appleIcon.getAttribute("href") !== next) appleIcon.setAttribute("href", next);
  }

  // Browser-tab favicon: swap to the tenant's public raster logo when
  // available so subdomain visitors see the right icon in their tab.
  // Non-public logos (private /objects/ paths) can't be fetched without a
  // session, so we leave the platform favicon in place for those cases.
  const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (faviconLink) {
    const next = isRaster ? src! : `${base}${METALUI_BRAND.faviconFile}`;
    if (faviconLink.getAttribute("href") !== next) faviconLink.setAttribute("href", next);
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  // Subdomain pre-auth branding: on `<slug>.metalui.co.uk` we need the
  // tenant's brand BEFORE Clerk resolves a session. We initialise from the
  // per-slug localStorage cache (synchronous — already read at module-eval
  // time into INITIAL_SUBDOMAIN_BRANDING) and then fetch the public endpoint
  // to refresh + write-through. Once the authed `/api/branding` response
  // arrives (q.data), it takes precedence and this state is no longer used.
  const [subdomainBranding, setSubdomainBranding] = useState<Branding | null>(
    INITIAL_SUBDOMAIN_BRANDING,
  );

  // /branding is mounted before the approved-user gate so this works for
  // pending users too. retry: false avoids a noisy retry storm before sign-in.
  // placeholderData: keepPreviousData keeps the last successful payload visible
  // through transient 401s (Clerk JWT rotation race) and through cache
  // invalidations after Save — without it, `q.data` flips to undefined on
  // every refetch, the provider falls back to PLATFORM_DEFAULT_BRANDING,
  // applyBranding() rips out every CSS var, and the page visually reverts
  // to navy defaults even though the operator's row is intact in the DB.
  // Active tenant scope. Branding is bound to `req.tenantId` server-side, which
  // the API client derives from the SELECTED tenant (`vc.selectedTenantId`), not
  // simply the first membership — a multi-membership user can have tenant B
  // selected while B is not index 0. We MUST key the localStorage cache by that
  // same selected tenant, validated against the live memberships (mirroring
  // TenantSelectionBridge), or a cold load for tenant B would briefly hydrate
  // tenant A's cached brand during the loading/error window AND write B's brand
  // back under A's key. The query key stays un-scoped because switching tenants
  // hard-reloads the page (see user-menu), so within one render lifetime there
  // is only ever one active tenant.
  const { me } = useMe();
  const scopeTenantId = useMemo(() => {
    const tenants = me?.tenants ?? [];
    if (tenants.length === 0) return null;
    const selected = getSelectedTenantId();
    if (selected && tenants.some((t) => t.id === selected)) return selected;
    return tenants[0]?.id ?? null;
  }, [me]);

  const q = useGetBranding({
    query: {
      queryKey: ["/api/branding"],
      staleTime: 60_000,
      retry: false,
      placeholderData: (prev) => prev,
    },
  });

  const value: Branding = useMemo(() => {
    const d = q.data as Partial<Branding> | undefined;
    if (!d) {
      // No live payload (still loading, or the request FAILED — e.g. an
      // unprovisioned tenant whose /api/branding returns 503). In BOTH cases
      // the fallback priority is:
      //   1. THIS tenant's own cache (scopeTenantId-keyed) — never bleeds across tenants.
      //   2. Subdomain pre-auth branding (slug-keyed) — applies on metalui.co.uk
      //      subdomains before auth resolves and scopeTenantId is known.
      //   3. Platform default.
      return (
        readCachedBranding(scopeTenantId) ??
        subdomainBranding ??
        PLATFORM_DEFAULT_BRANDING
      );
    }
    const out: Branding = {
      appName: d.appName || PLATFORM_DEFAULT_BRANDING.appName,
      logoUrl: d.logoUrl ?? null,
      primaryColor: d.primaryColor || PLATFORM_DEFAULT_BRANDING.primaryColor,
      secondaryColor: d.secondaryColor || PLATFORM_DEFAULT_BRANDING.secondaryColor,
      appNameColor: typeof d.appNameColor === "string" && d.appNameColor ? d.appNameColor : null,
      accentColor: null,
      destructiveColor: null,
      successColor: null,
      warningColor: null,
      backgroundColor: null,
      foregroundColor: null,
      mutedColor: null,
      borderColor: null,
      sidebarColor: null,
      sidebarForegroundColor: null,
      sidebarAccentColor: null,
      chartColor1: null,
      chartColor2: null,
      chartColor3: null,
      chartColor4: null,
      chartColor5: null,
      linkVisitedColor: null,
    };
    for (const k of EXTENDED_COLOR_KEYS) {
      const v = d[k];
      if (typeof v === "string") out[k] = v;
    }
    return out;
  }, [q.data, scopeTenantId, subdomainBranding]);

  // Pre-auth subdomain branding fetch. Runs once on mount when we detect a
  // metalui.co.uk subdomain. Uses a plain `fetch` (not `customFetch`) because
  // Clerk has not established a session yet — the endpoint is public. The
  // result is written to the per-slug cache so future page-loads get the
  // cached brand synchronously on first paint (zero round-trip).
  useEffect(() => {
    if (!SUBDOMAIN_SLUG) return;
    const slug = SUBDOMAIN_SLUG;
    const base = import.meta.env.BASE_URL as string;

    fetch(`${base}api/public/branding?slug=${encodeURIComponent(slug)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data: Partial<Branding> | null) => {
        if (!data) return;
        const b = shapeBrandingFromPublic(data);
        setSubdomainBranding(b);
        writeCachedSubdomainBranding(b, slug);
      })
      .catch(() => {
        // Network errors are non-fatal — the cached / platform-default
        // branding already applied. The user will see the correct brand
        // once auth resolves and /api/branding responds.
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty: SUBDOMAIN_SLUG is module-level constant

  useEffect(() => {
    applyBranding(value);
    // Repoint the installable-PWA surfaces (manifest link + iOS head tags +
    // browser-tab favicon) at this tenant's brand. Safe to run on every
    // branding change; it no-ops when the attributes already match.
    applyPwaHead(value, scopeTenantId);
    // Write-through to THIS tenant's cache so the NEXT cold load hydrates with
    // the right brand on first paint. Only cache values that came from the API
    // (q.data), and only once we know which tenant they belong to — otherwise
    // we'd risk caching the platform default under a stale pointer.
    if (q.data && scopeTenantId) writeCachedBranding(value, scopeTenantId);
  }, [value, q.data, scopeTenantId]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/**
 * Public pages may use tenant branding on a tenant subdomain, but must never
 * mount the operator branding query (which calls /api/me and /api/branding).
 * This provider only refreshes from the anonymous public branding endpoint.
 */
export function PublicBrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(
    INITIAL_SUBDOMAIN_BRANDING ?? PLATFORM_DEFAULT_BRANDING,
  );

  useEffect(() => {
    if (!SUBDOMAIN_SLUG) return;
    let active = true;
    const base = import.meta.env.BASE_URL as string;

    fetch(`${base}api/public/branding?slug=${encodeURIComponent(SUBDOMAIN_SLUG)}`)
      .then((response) => response.ok ? response.json() : null)
      .then((data: Partial<Branding> | null) => {
        if (!active || !data) return;
        const next = shapeBrandingFromPublic(data);
        setBranding(next);
        writeCachedSubdomainBranding(next, SUBDOMAIN_SLUG);
      })
      .catch(() => {
        // Cached or platform branding remains available if the public endpoint
        // is temporarily unavailable.
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!SUBDOMAIN_SLUG) return;
    applyBranding(branding, false);
    applyPwaHead(branding, null);
  }, [branding]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): Branding {
  return useContext(BrandingContext);
}
